// src/scenes/restaurant/BoissonsPage/BoissonsPage.jsx
//
// Carte des boissons. Aligné sur le modèle actuel :
//   - `cout_revient` : prix d'achat par unité vendue → sert au calcul du bénéfice
//   - `gerer_stock` + `stock` : suivi de stock OPTIONNEL, propre à la boisson
//     (plus aucun lien avec le module inventory)
//   - `nombre_ventes` n'existe plus : les volumes viennent de l'agrégation des
//     lignes de commande, visibles dans le dashboard.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Switch, FormControlLabel,
  Card, CardContent, CircularProgress,
  Avatar, FormControl, InputLabel, Select,
  InputAdornment, Tab, Tabs, Alert, Tooltip, Divider,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md` au profit
// de `size={{...}}`. Tout l'admin est écrit avec l'ancienne API → on importe la
// version historique, qui les accepte toujours.
import { GridLegacy as Grid } from '@mui/material';
import {
  Edit, Delete, Add, LocalCafe,
  Visibility, VisibilityOff, Search,
  Inventory, Image as ImageIcon, Liquor,
  Warning, Save, Percent,
} from '@mui/icons-material';
import { boissonAPI, categorieBoissonAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const TYPES = [
  { value: 'simple', label: 'Simple' },
  { value: 'cocktail', label: 'Cocktail' },
];

// En dessous, on considère qu'il faut réapprovisionner.
const SEUIL_STOCK_BAS = 5;

const FORM_VIDE = {
  id: null,
  nom: '',
  description: '',
  categorie: '',
  type_boisson: 'simple',
  prix: '',
  cout_revient: '',
  gerer_stock: false,
  stock: '',
  est_disponible: true,
  image: null,
  imagePreview: null,
};

const fmt = (n) => new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(n) || 0);

// Marge en % du prix de vente. null si le prix est absent : afficher 0 %
// laisserait croire à une marge nulle alors qu'elle est juste incalculable.
const marge = (prix, cout) => {
  const p = Number(prix) || 0;
  if (p <= 0) return null;
  return ((p - (Number(cout) || 0)) / p) * 100;
};

const couleurMarge = (m) => {
  if (m == null) return 'text.secondary';
  if (m < 0) return 'error.main';
  if (m < 20) return 'warning.main';
  return 'success.main';
};

// L'API est paginée (DRF) sur les listes ; certaines actions renvoient un
// tableau nu. On accepte les deux.
const versTableau = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const messageErreur = (error, defaut) => {
  const d = error.response?.data;
  if (!d) return error.message || defaut;
  if (typeof d === 'string') return d;
  if (d.detail) return d.detail;
  // Erreurs de validation DRF : { champ: ["message"] }
  const premier = Object.entries(d)[0];
  if (premier) {
    const [champ, valeur] = premier;
    const texte = Array.isArray(valeur) ? valeur[0] : valeur;
    return `${champ} : ${texte}`;
  }
  return defaut;
};

const BoissonsPage = () => {
  const [boissons, setBoissons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(FORM_VIDE);

  const fetchBoissons = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await boissonAPI.getAll();
      setBoissons(versTableau(response.data));
    } catch (err) {
      const msg = messageErreur(err, 'Erreur lors du chargement des boissons');
      setError(msg);
      toast.error(msg);
      setBoissons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categorieBoissonAPI.getAll();
      setCategories(versTableau(response.data).filter((c) => c.est_active));
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors du chargement des catégories'));
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchBoissons();
    fetchCategories();
  }, [fetchBoissons, fetchCategories]);

  const handleOpenDialog = (boisson = null) => {
    if (boisson) {
      // On ne reprend que les champs éditables : `code`, `categorie_nom` et les
      // dates sont en lecture seule côté serveur.
      setFormData({
        id: boisson.id,
        nom: boisson.nom || '',
        description: boisson.description || '',
        categorie: boisson.categorie || '',
        type_boisson: boisson.type_boisson || 'simple',
        prix: boisson.prix ?? '',
        cout_revient: boisson.cout_revient ?? '',
        gerer_stock: !!boisson.gerer_stock,
        stock: boisson.stock ?? '',
        est_disponible: !!boisson.est_disponible,
        image: null,
        imagePreview: boisson.image || null,
      });
      setIsEditing(true);
    } else {
      setFormData(FORM_VIDE);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((f) => ({ ...f, image: file, imagePreview: URL.createObjectURL(file) }));
    }
  };

  // Multipart seulement s'il y a un nouveau fichier ; sinon JSON, plus simple
  // à valider côté DRF.
  const construirePayload = () => {
    const champs = {
      nom: formData.nom.trim(),
      description: formData.description || '',
      categorie: formData.categorie,
      type_boisson: formData.type_boisson,
      prix: Number(formData.prix) || 0,
      cout_revient: Number(formData.cout_revient) || 0,
      gerer_stock: formData.gerer_stock,
      // Le stock n'a de sens que si le suivi est activé
      stock: formData.gerer_stock ? (Number(formData.stock) || 0) : 0,
      est_disponible: formData.est_disponible,
    };

    if (!(formData.image instanceof File)) return champs;

    const fd = new FormData();
    Object.entries(champs).forEach(([cle, valeur]) => fd.append(cle, valeur));
    fd.append('image', formData.image);
    return fd;
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) return toast.error('Le nom de la boisson est requis');
    if (!formData.categorie) return toast.error('La catégorie est requise');
    if (!formData.prix || Number(formData.prix) <= 0) {
      return toast.error('Le prix doit être supérieur à 0');
    }
    if (Number(formData.cout_revient) > Number(formData.prix)) {
      return toast.error('Le coût de revient dépasse le prix de vente');
    }

    setSubmitting(true);
    try {
      const payload = construirePayload();
      if (isEditing) {
        await boissonAPI.update(formData.id, payload);
        toast.success('Boisson modifiée');
      } else {
        await boissonAPI.create(payload);
        toast.success('Boisson créée');
      }
      setOpenDialog(false);
      setFormData(FORM_VIDE);
      fetchBoissons();
    } catch (err) {
      toast.error(messageErreur(err, "Erreur lors de l'enregistrement"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (boisson) => {
    if (!window.confirm(`Supprimer « ${boisson.nom} » ?`)) return;
    try {
      await boissonAPI.delete(boisson.id);
      toast.success('Boisson supprimée');
      fetchBoissons();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors de la suppression'));
    }
  };

  const toggleStatus = async (boisson) => {
    try {
      // PATCH : un PUT exigerait nom, catégorie et prix
      await boissonAPI.patch(boisson.id, { est_disponible: !boisson.est_disponible });
      toast.success(`Boisson ${!boisson.est_disponible ? 'activée' : 'désactivée'}`);
      fetchBoissons();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors du changement de statut'));
    }
  };

  const categoriesById = useMemo(
    () => new Map(categories.map((c) => [c.id, c.nom])),
    [categories],
  );

  const filtrees = useMemo(() => {
    const recherche = searchTerm.toLowerCase();
    return boissons.filter((b) => {
      const correspondRecherche =
        b.nom?.toLowerCase().includes(recherche) ||
        b.code?.toLowerCase().includes(recherche) ||
        false;
      const correspondCategorie =
        selectedCategory === 'all' || b.categorie === Number(selectedCategory);
      const correspondType = selectedType === 'all' || b.type_boisson === selectedType;
      const correspondStatut = activeTab === 0 ? b.est_disponible : !b.est_disponible;
      return correspondRecherche && correspondCategorie && correspondType && correspondStatut;
    });
  }, [boissons, searchTerm, selectedCategory, selectedType, activeTab]);

  const stats = useMemo(() => {
    const suivies = boissons.filter((b) => b.gerer_stock);
    const marges = boissons
      .map((b) => marge(b.prix, b.cout_revient))
      .filter((m) => m != null);
    return {
      total: boissons.length,
      disponibles: boissons.filter((b) => b.est_disponible).length,
      margeMoyenne: marges.length
        ? marges.reduce((s, m) => s + m, 0) / marges.length
        : null,
      aReapprovisionner: suivies.filter((b) => Number(b.stock) <= SEUIL_STOCK_BAS).length,
      sansCout: boissons.filter((b) => !Number(b.cout_revient)).length,
    };
  }, [boissons]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={<Button variant="outlined" size="small" onClick={fetchBoissons}>Réessayer</Button>}
        >
          <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <LocalCafe sx={{ mr: 2, verticalAlign: 'middle' }} />
              Boissons
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Prix, coût de revient et stock de votre carte des boissons
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher (nom ou code)..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ height: 40, px: 3 }}
            >
              Nouvelle boisson
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Total boissons</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Disponibles</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{stats.disponibles}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Marge moyenne</Typography>
                <Typography variant="h4" fontWeight="bold" color={couleurMarge(stats.margeMoyenne)}>
                  {stats.margeMoyenne == null ? '—' : `${stats.margeMoyenne.toFixed(1)} %`}
                </Typography>
                {stats.sansCout > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {stats.sansCout} sans coût saisi
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">À réapprovisionner</Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={stats.aReapprovisionner > 0 ? 'error.main' : 'text.primary'}
                >
                  {stats.aReapprovisionner}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  stock suivi ≤ {SEUIL_STOCK_BAS}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={selectedCategory}
              label="Catégorie"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="all">Toutes les catégories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.nom}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select value={selectedType} label="Type" onChange={(e) => setSelectedType(e.target.value)}>
              <MenuItem value="all">Tous les types</MenuItem>
              {TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { minHeight: 48 } }}>
          <Tab icon={<Visibility />} label="Disponibles" iconPosition="start" />
          <Tab icon={<VisibilityOff />} label="Indisponibles" iconPosition="start" />
        </Tabs>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Catégorie</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Prix</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Coût</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Marge</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Stock</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {searchTerm || selectedCategory !== 'all' || selectedType !== 'all'
                        ? 'Aucune boisson ne correspond aux critères'
                        : 'Aucune boisson dans cette liste'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtrees.map((boisson) => {
                  const m = marge(boisson.prix, boisson.cout_revient);
                  const stockBas = boisson.gerer_stock && Number(boisson.stock) <= SEUIL_STOCK_BAS;
                  return (
                    <TableRow key={boisson.id} hover>
                      <TableCell>
                        <Avatar
                          src={boisson.image || undefined}
                          variant="rounded"
                          sx={{ width: 56, height: 56, backgroundColor: 'action.hover' }}
                        >
                          {!boisson.image && <LocalCafe />}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">{boisson.code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{boisson.nom}</Typography>
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                          {boisson.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={boisson.categorie_nom || categoriesById.get(boisson.categorie) || '—'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {boisson.type_boisson === 'cocktail'
                            ? <Liquor fontSize="small" />
                            : <LocalCafe fontSize="small" />}
                          <Typography variant="body2">
                            {TYPES.find((t) => t.value === boisson.type_boisson)?.label || boisson.type_boisson}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{fmt(boisson.prix)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {Number(boisson.cout_revient) > 0 ? (
                          <Typography>{fmt(boisson.cout_revient)}</Typography>
                        ) : (
                          <Tooltip title="Sans coût de revient, le bénéfice de cette boisson est faussé">
                            <Chip label="à saisir" size="small" color="warning" variant="outlined" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={couleurMarge(m)}>
                          {m == null ? '—' : `${m.toFixed(0)} %`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {!boisson.gerer_stock ? (
                          <Typography variant="body2" color="textSecondary">Non suivi</Typography>
                        ) : (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Inventory fontSize="small" color={stockBas ? 'error' : 'success'} />
                            <Typography color={stockBas ? 'error.main' : 'success.main'} fontWeight="medium">
                              {fmt(boisson.stock)}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!boisson.est_disponible}
                              onChange={() => toggleStatus(boisson)}
                              size="small"
                            />
                          }
                          label={
                            <Chip
                              label={boisson.est_disponible ? 'Disponible' : 'Indisponible'}
                              size="small"
                              color={boisson.est_disponible ? 'success' : 'default'}
                              variant="outlined"
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton color="primary" onClick={() => handleOpenDialog(boisson)} size="small">
                            <Edit />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDelete(boisson)} size="small">
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Création / modification */}
      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalCafe />
            <Typography variant="h6">
              {isEditing ? 'Modifier la boisson' : 'Nouvelle boisson'}
            </Typography>
          </Box>
        </DialogTitle>
        {/* `dividers` évite que les libellés flottants de la 1re rangée soient
            rognés par le haut du dialogue. Les champs sont groupés par sujet :
            identité → tarification → stock → visuel. */}
        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* ── Identité ────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary">Identité</Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                label="Nom de la boisson"
                fullWidth
                size="small"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                required
                error={!formData.nom.trim()}
                helperText={!formData.nom.trim() ? 'Ce champ est requis' : 'Code généré automatiquement'}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.categorie}
                  label="Catégorie"
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  disabled={submitting}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.nom}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type_boisson}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type_boisson: e.target.value })}
                  disabled={submitting}
                >
                  {TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la boisson..."
                disabled={submitting}
              />
            </Grid>

            {/* ── Tarification ────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Tarification</Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Prix de vente"
                type="number"
                fullWidth
                size="small"
                value={formData.prix}
                onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                  inputProps: { step: '0.01', min: '0' },
                }}
                required
                error={!formData.prix || Number(formData.prix) <= 0}
                helperText={!formData.prix || Number(formData.prix) <= 0 ? 'Doit être supérieur à 0' : ' '}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Coût de revient"
                type="number"
                fullWidth
                size="small"
                value={formData.cout_revient}
                onChange={(e) => setFormData({ ...formData, cout_revient: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                  inputProps: { step: '0.01', min: '0' },
                }}
                error={Number(formData.cout_revient) > Number(formData.prix)}
                helperText={
                  Number(formData.cout_revient) > Number(formData.prix)
                    ? 'Supérieur au prix de vente'
                    : "Prix d'achat par unité vendue"
                }
                disabled={submitting}
              />
            </Grid>

            {/* Marge calculée en direct, alignée avec les deux champs qui la produisent */}
            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{ px: 1.5, py: 1, height: 40, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Percent fontSize="small" sx={{ color: 'text.secondary' }} />
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.1}>
                    Marge
                  </Typography>
                  <Typography
                    fontWeight="bold"
                    lineHeight={1.2}
                    color={couleurMarge(marge(formData.prix, formData.cout_revient))}
                  >
                    {marge(formData.prix, formData.cout_revient) == null
                      ? '—'
                      : `${marge(formData.prix, formData.cout_revient).toFixed(1)} %`}
                    <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                      ({fmt((Number(formData.prix) || 0) - (Number(formData.cout_revient) || 0))} HTG)
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* ── Stock & disponibilité ───────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Stock & disponibilité</Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.gerer_stock}
                      onChange={(e) => setFormData({ ...formData, gerer_stock: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Suivre le stock</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Compté dans l'unité de vente (bouteilles, canettes, verres…)
                      </Typography>
                    </Box>
                  }
                />
                {formData.gerer_stock && (
                  <TextField
                    label="Quantité en stock"
                    type="number"
                    size="small"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    InputProps={{ inputProps: { step: '1', min: '0' } }}
                    disabled={submitting}
                    sx={{ mt: 2, maxWidth: 220 }}
                  />
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_disponible}
                      onChange={(e) => setFormData({ ...formData, est_disponible: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Disponible à la vente</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Visible sur la carte du caissier
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>

            {/* ── Visuel ──────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Visuel</Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={formData.imagePreview || undefined}
                  variant="rounded"
                  sx={{ width: 72, height: 72, border: '1px solid', borderColor: 'divider' }}
                >
                  <LocalCafe />
                </Avatar>
                <Box>
                  <Button variant="outlined" component="label" startIcon={<ImageIcon />} disabled={submitting}>
                    Choisir une image
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} disabled={submitting} />
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Optionnel — affichée sur la carte
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => !submitting && setOpenDialog(false)} variant="outlined" disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.nom.trim() || !formData.categorie || !formData.prix || Number(formData.prix) <= 0}
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Save /> : <Add />)}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoissonsPage;
